import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    Grid,
    InputAdornment,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import { AssignmentTwoTone, CheckCircle, ExpandMore, Visibility, Warning } from "@mui/icons-material";
import { orange } from "@mui/material/colors";
import axios from "axios";
// Imported from the explicit dist/cjs subpath (not the bare "react-pdf" specifier): the
// package's "exports" map sends plain `import` syntax to its ESM build, whose internal
// imports aren't extension-specified and which webpack 5 under an unejected CRA config
// can't resolve. The literal cjs path bypasses that conditional resolution.
import { Document, Page, pdfjs } from "react-pdf/dist/cjs/index.js";
import BlurredBackdrop from "../../../components/BlurredBackdrop";
import ResizableSignatureCanvas from "../../../components/ResizableSignatureCanvas";
import MemberService from "../../../services/MemberService";
import { useAuth } from "../../../providers/AuthProvider";
import { base64ToBlob, contentTypeFromExtension, downloadBlobFile } from "../../../util/FileDownload";

// Edge (and some other browsers) can be configured to always download PDFs rather than
// render them, which makes an <iframe src="blob:..."> of a PDF show a download prompt
// instead of the document. Rendering pages via pdf.js onto <canvas> sidesteps the
// browser's native PDF handling entirely, so it works regardless of that setting.
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

// pdf.js needs its own "standard font" glyph data to draw the 14 built-in PDF fonts (e.g.
// ZapfDingbats, used here for checkbox/checkmark glyphs) when a PDF references one without
// embedding it. Without this, it fails to fetch that data and warns in the console — copied
// into public/pdfjs/standard_fonts from node_modules/pdfjs-dist/standard_fonts since these
// files are fetched by URL at runtime with filenames pdf.js appends itself, so they can't be
// resolved through webpack's bundler the way the worker script above is.
// Module-level (not created inside the component): react-pdf warns if the "options" prop
// passed to <Document> is a new object identity on every render.
const PDF_DOCUMENT_OPTIONS = {
    standardFontDataUrl: `${process.env.PUBLIC_URL}/pdfjs/standard_fonts/`,
};

// Catches exceptions thrown during pdf.js's canvas rendering (e.g. a corrupt page, or the
// worker script failing to load) that never reach react-pdf's own onLoadError/onRenderError
// callbacks. Function components can't catch render errors in their children, so this has
// to be a class component. Callers should pass a `key` tied to the document being viewed so
// React remounts (and clears hasError) whenever a new document is opened.
class PdfViewerErrorBoundary extends React.Component {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error) {
        this.props.onError(error);
    }

    render() {
        return this.state.hasError ? null : this.props.children;
    }
}

const emptyCompRow = () => ({ name: "", salary: "", allowances: "", other: "" });

const getLocalDateInputValue = () => {
    const now = new Date();
    const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return localTime.toISOString().split("T")[0];
};

const emptyForm = () => ({
    // Part A
    orgName: "",
    regNumber1: "",
    regNumber2: "",
    regNumber3: "",
    orderMonth: "",
    orderDay: "",
    orderYear: "",
    initiationFee: "",
    duesMonthly: "",
    duesBiweekly: "",
    duesWeekly: "",
    duesAnnually: "",
    compensation: [
        emptyCompRow(),
        emptyCompRow(),
        emptyCompRow(),
        emptyCompRow(),
        emptyCompRow(),
    ],
    // Part B
    employeeName: "",
    publicEmployer: "",
    agencyName: "",
    agencySameAsEmployer: true,
    classTitle: "",
    classCode: "",
    classCodeNA: false,
    employeeId: "",
    LocChapFEAID: "",
    signature: "",
    signatureDate: getLocalDateInputValue(),
});

const normalizeKey = (value = "") => String(value).toLowerCase().replace(/[^a-z0-9]/g, "");

const valueToText = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    return String(value);
};

const valueToBool = (value) => value === true || String(value).toLowerCase() === "true";

const rowSx = { borderBottom: "1px solid", borderColor: "divider", py: 1.25, px: 1.5 };
const colBorder = { borderRight: "1px solid", borderColor: "divider" };

const findInfoFieldValue = (infoFields = {}, candidateKeys = []) => {
    const normalizedCandidates = candidateKeys.map(normalizeKey);

    const matchingKey = Object.keys(infoFields || {}).find((key) =>
        normalizedCandidates.includes(normalizeKey(key))
    );

    if (!matchingKey) {
        return "";
    }

    return valueToText(infoFields[matchingKey]);
};

const buildPrefilledForm = (membershipMaf, localUnionName = "", employer = null, individual = null, localUnionLocChapFEAID = "") => {
    const base = emptyForm();
    if (!membershipMaf) {
        return base;
    }

    const membershipMafInfoFields = membershipMaf.infoFields || {};
    const mafSettings = membershipMaf.mafSettings || null;
    const mafSettingsInfoFields = mafSettings?.infoFields || {};

    const compensation = Array.from({ length: 5 }, (_, i) => {
        const rowNum = i + 1;
        return {
            name: findInfoFieldValue(mafSettingsInfoFields, [`OfficerCompensationName${rowNum}`]) || "",
            salary: findInfoFieldValue(mafSettingsInfoFields, [`OfficerCompensationSalary${rowNum}`]) || "",
            allowances: findInfoFieldValue(mafSettingsInfoFields, [`OfficerCompensationAllowances${rowNum}`]) || "",
            other: findInfoFieldValue(mafSettingsInfoFields, [`OfficerCompensationOtherDisbursements${rowNum}`]) || "",
        };
    });
    const dateParts = {
        month: findInfoFieldValue(mafSettingsInfoFields, ["DateofLastOrderMonth"]),
        day: findInfoFieldValue(mafSettingsInfoFields, ["DateofLastOrderDay"]),
        year: findInfoFieldValue(mafSettingsInfoFields, ["DateofLastOrderYear"]),
    };
    const publicEmployer = valueToText(employer?.name || "");
    const agencySameAsEmployerRaw = findInfoFieldValue(mafSettingsInfoFields, ["IsAgencyNamesameasPublicEmployer"]);
    // Default to checked (agency same as public employer) when the Local hasn't set this explicitly.
    const agencySameAsEmployer = agencySameAsEmployerRaw === "" ? true : valueToBool(agencySameAsEmployerRaw);

    // mafSettings.ClassTitle/.ClassCode are the LocalChapter's full lists of available options
    // (used to build the autocomplete dropdowns) — not the member's selection, so they must not
    // be used as a default here. Only a value the member already saved on their own MembershipMAF
    // should prefill these fields; otherwise they stay blank until the member picks one.
    const classTitle = findInfoFieldValue(membershipMafInfoFields, ["ClassTitle", "ClassName", "JobClassTitle"]);
    const classCode = findInfoFieldValue(membershipMafInfoFields, ["ClassCode", "JobClassCode"]);
    const employeeId = valueToText(membershipMaf?.employeeId || findInfoFieldValue(membershipMafInfoFields, ["EmployeeID", "EmployeeId", "EmployeeNumber"]));
    const localChapterFeaId = valueToText(localUnionLocChapFEAID || membershipMaf?.LocChapFEAID || "");

    return {
        ...base,
        // NameofEmployeeOrganization is the dedicated MAF settings field for this - falls back to
        // the LocalChapter's own DisplayName (localUnionName) for older records that predate the
        // field. membershipMaf.name is deliberately excluded from this chain: it's that entity's
        // own DisplayName (e.g. "MAF_<id>_<lastname>_<firstname>", or a legacy placeholder on
        // older records), never the employee organization's name.
        orgName: valueToText(
            findInfoFieldValue(mafSettingsInfoFields, ["NameofEmployeeOrganization"])
            || localUnionName
        ),
        regNumber1: findInfoFieldValue(mafSettingsInfoFields, ["PERCRegYear"]),
        regNumber2: findInfoFieldValue(mafSettingsInfoFields, ["PERCRegSequence"]),
        regNumber3: "",
        orderMonth: dateParts.month,
        orderDay: dateParts.day,
        orderYear: dateParts.year,
        initiationFee: findInfoFieldValue(mafSettingsInfoFields, ["InitiationFee"]),
        duesMonthly: findInfoFieldValue(mafSettingsInfoFields, ["DuesAmountMonthly"]),
        duesBiweekly: findInfoFieldValue(mafSettingsInfoFields, ["DuesAmountBiWeekly"]),
        duesWeekly: findInfoFieldValue(mafSettingsInfoFields, ["DuesAmountWeekly"]),
        duesAnnually: findInfoFieldValue(mafSettingsInfoFields, ["DuesAmountAnnually"]),
        compensation,
        employeeName: [
            valueToText(individual?.firstName),
            valueToText(individual?.middleName),
            valueToText(individual?.lastName),
        ].filter(Boolean).join(" ") || findInfoFieldValue(membershipMafInfoFields, ["EmployeeName", "MemberName"]),
        publicEmployer,
        agencyName: "",
        agencySameAsEmployer,
        classTitle,
        classCode,
        classCodeNA: false,
        employeeId,
        LocChapFEAID: localChapterFeaId,
        signatureDate: base.signatureDate,
    };
};

// Identifies one membership/NME item across refreshes — matched by the underlying
// membership's id when present, falling back to the NME's id otherwise.
const itemKey = (item) => item?.membership?.id ?? item?.nme?.id;

// Same "completed" check MembershipAuthorizationCard uses for its own status label.
const isItemMafCompleted = (item) => {
    const membershipMaf = item?.membershipMaf ?? item?.membership?.membershipMaf ?? null;
    return valueToText(membershipMaf?.status?.name).trim().toLowerCase() === "completed";
};

// Not Completed forms first, Completed last. Array.prototype.sort is stable, so the
// backend's order is kept within each group.
const sortByMafStatus = (items = []) =>
    [...items].sort((a, b) => Number(isItemMafCompleted(a)) - Number(isItemMafCompleted(b)));

// Same visibility rules MembershipAuthorizationCard applies to itself (its isFormDisabled
// check): hidden when the MAF is missing, Inactive, or the Local has MAF disabled (the
// backend nulls out mafSettings - see ResolveMafSettings in PortalController.cs). Lifted
// here so the parent can skip the shared "Member Authorization Form" card entirely when
// no item would render.
const isItemMafVisible = (item) => {
    const membershipMaf = item?.membershipMaf ?? item?.membership?.membershipMaf ?? null;
    const isMembershipMafMissing =
        !membershipMaf ||
        (typeof membershipMaf === "object" && Object.keys(membershipMaf).length === 0);
    const isInactive = valueToText(membershipMaf?.status?.name).trim().toLowerCase() === "inactive";
    return !isMembershipMafMissing && !isInactive && !!membershipMaf?.mafSettings;
};

function MemberAuthorization({ memberships = [] }) {
    const [membershipMafData, setMembershipMafData] = useState(memberships);
    const { user } = useAuth();

    useEffect(() => {
        setMembershipMafData(memberships);
    }, [memberships]);

    // Shared by every card below so that submitting one card's form refreshes every
    // card's status (an individual could have data that shifts across memberships).
    const refreshMembershipMafStatus = async () => {
        if (!user?.id) {
            return null;
        }

        try {
            const latestMafData = await MemberService.loadMembershipMaf(user.id);
            setMembershipMafData(latestMafData || []);
            return latestMafData || [];
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Couldn't refresh Membership MAF status.", error);
            }
            return null;
        }
    };

    const visibleItems = sortByMafStatus(membershipMafData.filter(isItemMafVisible));

    if (visibleItems.length === 0) {
        return null;
    }

    // One card with a single "Member Authorization Form" header; each membership/NME
    // item renders as a compact section inside it (not its own card + header), so
    // multiple forms stack tightly instead of repeating the header per row.
    return (
        <Card>
            <CardHeader title="Member Authorization Form" sx={{ pb: 0 }} />
            <CardContent>
                <Stack spacing={2}>
                    {visibleItems.map((item) => (
                        <MembershipAuthorizationCard
                            key={itemKey(item)}
                            item={item}
                            onSubmitted={refreshMembershipMafStatus}
                        />
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
}

// Renders one Member Authorization Form status section (plus its fill-in and signed-PDF
// viewer dialogs) for a single membership/NME item, inside the parent's shared card. An individual with more than one
// active membership gets one of these per item, each with fully independent dialog state.
function MembershipAuthorizationCard({ item, onSubmitted }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isPreparingView, setIsPreparingView] = useState(false);
    // Renders the signed PDF inline (via pdf.js, in its own Dialog) instead of opening it
    // in a new browser tab. Auto-viewing right after submit chains multiple awaited network
    // calls after the triggering click, which popup blockers treat as no longer coming from
    // a real user gesture and block outright; an in-page viewer never opens a new browsing
    // context, so it isn't subject to that blocking at all.
    const [viewerState, setViewerState] = useState({ open: false, blob: null, fileName: null, numPages: null, failed: false });
    // A callback ref (not a plain useRef read in an effect keyed on viewerState.open): MUI's
    // Dialog portals its content, and the Box below isn't necessarily attached to the DOM yet
    // by the time an effect merely keyed on "open" runs — that raced to a null ref in testing,
    // which silently left viewerWidth stuck at 0 forever (no <Page> ever rendered). A callback
    // ref instead fires exactly when React actually attaches (or detaches) the DOM node, so it
    // can't race the Dialog's own mount timing.
    const [viewerContainerNode, setViewerContainerNode] = useState(null);
    const viewerContainerRef = useCallback((node) => setViewerContainerNode(node), []);
    const [viewerWidth, setViewerWidth] = useState(0);

    // Measures from window resize events, not a ResizeObserver on the content box itself.
    // The box we'd otherwise observe is sized by what we render into it (<Page>'s canvas),
    // so any such observer is inherently a feedback loop: a width change resizes the canvas,
    // which can change the box's own size (e.g. a scrollbar appearing/disappearing as content
    // height crosses the box's height), which reports another width change, which cancels
    // pdf.js's in-progress render and restarts it — and if that never settles, the page never
    // finishes drawing. The window's size can never be caused by our own rendering, so listening
    // to it instead is immune to that loop by construction rather than by tuning around it.
    // useLayoutEffect + a synchronous read (not useEffect) so the very first <Page> render
    // already has the real width, instead of one render at a stale/zero default first.
    useLayoutEffect(() => {
        if (!viewerContainerNode) {
            setViewerWidth(0);
            return undefined;
        }
        // floor, not round: rounding up even a fraction of a pixel past the true available
        // width feeds <Page> a width it can't actually fit in, clipping/pushing the page off
        // one edge instead of centering it — flooring can only ever undershoot by a sliver,
        // which is invisible, never clip.
        const measure = () => setViewerWidth(Math.floor(viewerContainerNode.getBoundingClientRect().width));
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("orientationchange", measure);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("orientationchange", measure);
        };
    }, [viewerContainerNode]);

    const parentEntity = item?.membership ?? item?.nme ?? null;
    const membershipMaf = item?.membershipMaf ?? item?.membership?.membershipMaf ?? null;
    const isMembershipMafMissing =
        !membershipMaf ||
        (typeof membershipMaf === "object" && Object.keys(membershipMaf).length === 0);
    const localUnionName = item?.localUnion?.name ?? "";
    const employer = item?.localUnion?.employer ?? null;
    const localUnionLocChapFEAID = item?.localUnion?.LocChapFEAID ?? "";
    const individual = item?.individual ?? null;
    const mafSettings = membershipMaf?.mafSettings ?? null;
    const mafSettingsInfoFields = mafSettings?.infoFields ?? {};
    // Same precedence buildPrefilledForm below uses for the form's own "Name of Employee
    // Organization" field, so the card shows exactly what the Local set there. membershipMaf?.name
    // is deliberately excluded - it's that entity's own DisplayName (e.g. "MAF_<id>_<lastname>_
    // <firstname>", or a legacy placeholder on older records), never the organization's name.
    const employeeOrganizationName = valueToText(
        findInfoFieldValue(mafSettingsInfoFields, ["NameofEmployeeOrganization"])
        || localUnionName
    );
    const membershipMafStatus = valueToText(
        membershipMaf?.status?.name
    ).trim().toLowerCase();
    const isInactive = membershipMafStatus === "inactive";
    const submitted = membershipMafStatus === "completed";
    const submittedAttachment = membershipMaf?.downloadAttachment ?? null;
    // The backend nulls out membershipMaf.mafSettings (while membershipMaf itself still
    // exists) whenever the Local Chapter has MAF disabled (IsMAFEnabled = false) - see
    // ResolveMafSettings in PortalController.cs. Checked separately from
    // isMembershipMafMissing since membershipMaf is still present in that case.
    const isMafDisabledForLocal = !isMembershipMafMissing && !membershipMaf?.mafSettings;
    // isInactive/isMembershipMafMissing are only relevant up to this check: once it's
    // false, the card renders below with both of those guaranteed false (either one
    // hides the whole card via the isFormDisabled return further down), leaving submitted
    // vs. not-submitted as the only two states the rest of this component needs to handle.
    const isFormDisabled = isInactive || isMembershipMafMissing || isMafDisabledForLocal;
    const statusLabel = submitted ? "Completed" : "Not Completed";
    // Completed gets navy; Not Completed gets the same orange as the Payment Method card's
    // warning state (orange[400] icon, orange[500] alert border - see PaymentMethod/index.js
    // and PaymentMethodAlert.css) - so the icon next to the status and the alert's icon read
    // as the same signal, consistent with the rest of the portal.
    const navyColor = "#003087";
    const orangeIconColor = orange[400];
    const orangeBorderColor = orange[500];
    const StatusIcon = submitted ? CheckCircle : Warning;
    const statusIconColor = submitted ? navyColor : orangeIconColor;

    const isMobileDevice = useMediaQuery((theme) => theme.breakpoints.down("md"));

    const handleOpen = () => {
        setDialogOpen(true);
    };

    const handleViewSubmittedForm = async (attachmentInfo = submittedAttachment) => {
        if (!attachmentInfo?.entityId) {
            return;
        }

        setIsPreparingView(true);

        try {
            const attachment = await MemberService.downloadAttachment(attachmentInfo.entityId);
            const infoFields = attachment?.infoFields || {};
            const content = infoFields?.data?.initialValue;
            const contentType = infoFields?.contentType?.initialValue
                || contentTypeFromExtension(infoFields?.fileExtension?.initialValue, "application/pdf");
            const fileName = infoFields?.filename?.initialValue || attachmentInfo?.fileName || null;

            const blob = base64ToBlob(content, contentType);
            setViewerState({ open: true, blob, fileName, numPages: null, failed: false });
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Could not open Member Authorization attachment.", error);
            }
        } finally {
            setIsPreparingView(false);
        }
    };

    const closeViewer = () => {
        setViewerState({ open: false, blob: null, fileName: null, numPages: null, failed: false });
    };

    const handleViewerLoadSuccess = ({ numPages }) => {
        setViewerState((prevState) => ({ ...prevState, numPages }));
    };

    // Falls back to a plain download prompt if pdf.js can't load or render the document
    // (corrupt/unsupported file, the worker script failing to fetch, etc.) instead of
    // leaving the member looking at a blank or partially-rendered viewer.
    const handleViewerFailure = (error) => {
        console.error("Could not render Member Authorization attachment.", error);
        setViewerState((prevState) => ({ ...prevState, failed: true }));
    };

    const handleDownloadViewedForm = () => {
        if (!viewerState.blob) {
            return;
        }

        downloadBlobFile(viewerState.blob, viewerState.fileName || "Member-Authorization-Form.pdf");
    };

    const handlePrimaryButtonClick = () => {
        if (submitted) {
            handleViewSubmittedForm();
            return;
        }

        handleOpen();
    };

    const handleClose = (_, reason) => {
        if (reason !== "backdropClick") {
            setDialogOpen(false);
        }
    };

    const handleSubmitSuccess = async () => {
        setDialogOpen(false);
        const latestMafData = await onSubmitted();

        // View the just-submitted form right away instead of making the member click
        // "View Signed Form" separately. Matched back to THIS card's own item by id
        // (not array position) since the refreshed array can reorder or contain other
        // cards' items too.
        const myKey = itemKey(item);
        const latestItem = latestMafData?.find((candidate) => itemKey(candidate) === myKey) ?? latestMafData?.[0];
        const latestMembershipMaf = latestItem?.membershipMaf ?? latestItem?.membership?.membershipMaf ?? null;
        const latestAttachment = latestMembershipMaf?.downloadAttachment ?? null;
        if (latestAttachment?.entityId) {
            handleViewSubmittedForm(latestAttachment);
        }
    };

    if (isFormDisabled)
    {
        return null;
    }

    return (
        <>
            <Box>
                {/* Icon + Employee Organization + Status on one row (Status beside the
                    organization, not stacked under it) to keep each form's section short. */}
                <Grid container columnSpacing={2} alignItems="center" wrap="nowrap" sx={{ mb: 1 }}>
                    <Grid item xs="auto" sx={{ display: { xs: "none", sm: "block" } }}>
                        <Avatar sx={{ height: 40, width: 40 }}>
                            <AssignmentTwoTone sx={{ height: "60%", width: "60%" }} />
                        </Avatar>
                    </Grid>
                    {/* Two-row grid (titles on row 1, values on row 2) with two equal
                        columns: the "Employee Organization" and "Status" titles always line
                        up, even when a long organization name wraps on phone widths. */}
                    <Grid item xs sx={{ minWidth: 0 }}>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                                columnGap: 2,
                                alignItems: "start",
                            }}
                        >
                            <Typography variant="body2" fontWeight="bold">
                                Employee Organization
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                                Status
                            </Typography>
                            <Typography variant="overline" color="GrayText" sx={{ display: "block", lineHeight: 1.75 }}>
                                {employeeOrganizationName}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", columnGap: 0.5 }}>
                                <StatusIcon htmlColor={statusIconColor} sx={{ height: 14, width: 14, flexShrink: 0 }} />
                                <Typography variant="overline" color="GrayText" sx={{ display: "block", lineHeight: 1.75 }}>
                                    {statusLabel}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Alert
                    severity={submitted ? "info" : "warning"}
                    icon={submitted ? <CheckCircle fontSize="small" /> : <Warning fontSize="small" />}
                    action={
                        <Button
                            size="small"
                            variant="outlined"
                            color={submitted ? "info" : "warning"}
                            startIcon={submitted ? <Visibility /> : <AssignmentTwoTone />}
                            disableElevation
                            onClick={handlePrimaryButtonClick}
                            disabled={isPreparingView || (submitted && !submittedAttachment?.entityId)}
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            {submitted ? "View Signed Form" : "Complete Form"}
                        </Button>
                    }
                    sx={{
                        py: 0,
                        alignItems: "center",
                        border: "1px solid",
                        borderColor: submitted ? navyColor : orangeBorderColor,
                        ...(submitted
                            ? { bgcolor: "#e8eef7", color: navyColor, "& .MuiAlert-icon": { color: navyColor } }
                            : { bgcolor: "#fff4e5", color: "#663c00", "& .MuiAlert-icon": { color: orangeIconColor } }),
                        // Button sits to the right of the message (centered vertically) rather
                        // than on its own row underneath; on phone widths it wraps below.
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                        "& .MuiAlert-message": { fontSize: "0.8125rem", py: 0.75, flexBasis: { xs: "80%", sm: "auto" } },
                        "& .MuiAlert-action": {
                            alignItems: "center",
                            py: 0,
                            mr: 0,
                            pl: 1,
                            flexBasis: { xs: "100%", sm: "auto" },
                            justifyContent: "flex-end",
                            pb: { xs: 1, sm: 0 },
                        },
                    }}
                >
                    <AlertTitle sx={{ fontSize: "0.875rem", mb: 0.25 }}>
                        {submitted ? "Authorization form submitted" : "Authorization form required"}
                    </AlertTitle>
                    {submitted
                        ? "Your Member Authorization Form (PERC Form 2023-1.101) has been completed."
                        : "Please complete and sign the Employee Organization Membership Authorization Form (PERC Form 2023-1.101)."}
                </Alert>
            </Box>

            <Dialog
                open={dialogOpen}
                onClose={handleClose}
                fullScreen={isMobileDevice}
                maxWidth="md"
                fullWidth
                BackdropComponent={BlurredBackdrop}
                PaperProps={{
                    sx: {
                        height: { xs: "100%", md: "85vh" },
                        display: "flex",
                    },
                }}
            >
                <DialogContent dividers sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <MemberAuthorizationForm
                        onSubmit={handleSubmitSuccess}
                        onCancel={() => setDialogOpen(false)}
                        membershipMaf={membershipMaf}
                        membership={parentEntity}
                        localUnionName={localUnionName}
                        employer={employer}
                        localUnionLocChapFEAID={localUnionLocChapFEAID}
                        individual={individual}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={viewerState.open}
                onClose={closeViewer}
                fullScreen={isMobileDevice}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        height: { xs: "100%", md: "90vh" },
                        display: "flex",
                        flexDirection: "column",
                    },
                }}
            >
                <DialogContent dividers sx={{ display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            px: 2,
                            py: 1,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="subtitle2" noWrap>
                            {viewerState.fileName || "Member Authorization Form"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, flexShrink: 0 }}>
                            <Link component="button" onClick={handleDownloadViewedForm} sx={{ textDecoration: "none" }}>
                                Download
                            </Link>
                            <Link component="button" onClick={closeViewer} sx={{ textDecoration: "none" }}>
                                Close
                            </Link>
                        </Box>
                    </Box>
                    {viewerState.blob && viewerState.failed ? (
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                                p: 4,
                                textAlign: "center",
                            }}
                        >
                            <Typography variant="body1">
                                This document can&apos;t be previewed here. You can still download it below.
                            </Typography>
                            <Button variant="contained" onClick={handleDownloadViewedForm}>
                                Download {viewerState.fileName || "Member Authorization Form"}
                            </Button>
                        </Box>
                    ) : null}
                    {viewerState.blob && !viewerState.failed ? (
                        <PdfViewerErrorBoundary key={viewerState.blob} onError={handleViewerFailure}>
                            <Box
                                ref={viewerContainerRef}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    width: "100%",
                                    // overflowY: "scroll" (always reserving the scrollbar's width) rather
                                    // than "auto": with "auto", the vertical scrollbar appears only once a
                                    // page's rendered height overflows, which shrinks this box's measured
                                    // width, which re-renders <Page> narrower, which can make it short
                                    // enough that the scrollbar disappears again — an oscillation that keeps
                                    // cancelling pdf.js's in-progress render before it ever finishes, leaving
                                    // a blank canvas. A desktop scrollbar reserves layout space (triggering
                                    // this); a touch/overlay scrollbar doesn't, which is why this only showed
                                    // up outside of touch/mobile viewports. scrollbarGutter backs this up on
                                    // browsers where "scroll" alone doesn't reserve space for an empty track.
                                    overflowX: "hidden",
                                    overflowY: "scroll",
                                    scrollbarGutter: "stable",
                                    WebkitOverflowScrolling: "touch",
                                    bgcolor: "grey.200",
                                }}
                            >
                                {/* Hard CSS backstop: the width fed to <Page> below is only a target
                                    render resolution. However well that measurement is computed, it has
                                    repeatedly landed a hair off in one browser/device or another and
                                    clipped the page. max-width/height:auto on the canvas itself makes
                                    overflow structurally impossible regardless of that number's accuracy. */}
                                <style>{`
                                    .react-pdf__Page__canvas {
                                        max-width: 100% !important;
                                        height: auto !important;
                                    }
                                `}</style>
                                <Document
                                    file={viewerState.blob}
                                    options={PDF_DOCUMENT_OPTIONS}
                                    onLoadSuccess={handleViewerLoadSuccess}
                                    onLoadError={handleViewerFailure}
                                    loading={
                                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                                            <CircularProgress />
                                        </Box>
                                    }
                                >
                                    {viewerWidth > 0
                                        ? Array.from({ length: viewerState.numPages || 0 }, (_, index) => (
                                            <Box key={index + 1} sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                                                <Page
                                                    pageNumber={index + 1}
                                                    width={viewerWidth}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={false}
                                                    onRenderError={handleViewerFailure}
                                                />
                                            </Box>
                                        ))
                                        : null}
                                </Document>
                            </Box>
                        </PdfViewerErrorBoundary>
                    ) : null}
                </DialogContent>
            </Dialog>
        </>
    );
}

function MemberAuthorizationForm({ onSubmit, onCancel, membershipMaf, membership, localUnionName, employer, localUnionLocChapFEAID = "", individual }) {
    const signatureCanvasRef = useRef();
    const fieldValuesRef = useRef({});
    const cancelTokenRef = useRef(axios.CancelToken.source());
    const [validationError, setValidationError] = useState("");
    const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [memberAuthorizationHeaderHtml, setMemberAuthorizationHeaderHtml] = useState("");
    const [memberAuthorizationFloridaNoticeHtml, setMemberAuthorizationFloridaNoticeHtml] = useState("");
    const [memberAuthorizationInstructionsHtml, setMemberAuthorizationInstructionsHtml] = useState("");

    const classTitleRelationships =
        membershipMaf?.ClassTitle ||
        membershipMaf?.mafSettings?.ClassTitle ||
        membershipMaf?.relationships?.ClassTitle ||
        [];
    const classTitleOptions = classTitleRelationships
        .map(item => item.name || item.displayName || "")
        .filter(Boolean);

    const classCodeRelationships =
        membershipMaf?.ClassCode ||
        membershipMaf?.mafSettings?.ClassCode ||
        membershipMaf?.relationships?.ClassCode ||
        [];
    const classCodeOptions = classCodeRelationships
        .map(item => ({
            code: item.name || "",
            label: item.description ? `${item.name} - ${item.description}` : (item.name || ""),
        }))
        .filter(o => o.code);

    const getDefaultFormValues = () => buildPrefilledForm(
        membershipMaf,
        localUnionName,
        employer,
        individual,
        localUnionLocChapFEAID,
    );

    const { watch, setValue, getValues } = useForm({
        mode: "onChange",
        defaultValues: getDefaultFormValues()
    });

    useEffect(() => {
        fieldValuesRef.current = { ...getValues() };
    }, []);

    const formValues = watch();
    const agencySameAsEmployer = watch("agencySameAsEmployer");
    const classCodeNA = watch("classCodeNA");

    const [classTitleInput, setClassTitleInput] = useState(() => getValues("classTitle") || "");
    const [classCodeInput, setClassCodeInput] = useState(() => getValues("classCode") || "");

    useEffect(() => {
        const newFormValues = getDefaultFormValues();
        Object.keys(newFormValues).forEach(key => {
            setValue(key, newFormValues[key]);
            fieldValuesRef.current[key] = newFormValues[key];
        });
        setClassTitleInput(newFormValues.classTitle || "");
        setClassCodeInput(newFormValues.classCode || "");
    }, [membershipMaf, localUnionName, employer, localUnionLocChapFEAID, individual, setValue]);

    const loadHtmlSetting = useCallback((requestFn, setter, errorMessage) => {
        const source = axios.CancelToken.source();

        requestFn(source.token)
            .then((content) => {
                const htmlContent = valueToText(content).trim();
                setter(htmlContent || "");
            })
            .catch((error) => {
                if (!axios.isCancel(error)) {
                    console.error(errorMessage, error);
                }

                setter("");
            });

        return source;
    }, []);

    useEffect(() => {
        const source = loadHtmlSetting(
            (token) => MemberService.getMemberAuthorizationFormHeader(token),
            setMemberAuthorizationHeaderHtml,
            "Error loading Member Authorization form header:"
        );

        return () => {
            source.cancel();
        };
    }, [loadHtmlSetting]);

    useEffect(() => {
        const source = loadHtmlSetting(
            (token) => MemberService.getMemberAuthorizationFloridaNotice(token),
            setMemberAuthorizationFloridaNoticeHtml,
            "Error loading Member Authorization Florida notice:"
        );

        return () => {
            source.cancel();
        };
    }, [loadHtmlSetting]);

    useEffect(() => {
        const source = loadHtmlSetting(
            (token) => MemberService.getMemberAuthorizationFormInstructions(token),
            setMemberAuthorizationInstructionsHtml,
            "Error loading Member Authorization form instructions:"
        );

        return () => {
            source.cancel();
        };
    }, [loadHtmlSetting]);

    const set = useCallback((field) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setValue(field, value);
        fieldValuesRef.current[field] = value;
    }, [setValue]);

    const handleAgencySameAsEmployerChange = useCallback((e) => {
        const checked = e.target.checked;
        setValue("agencySameAsEmployer", checked);
        fieldValuesRef.current.agencySameAsEmployer = checked;

        if (checked) {
            setValue("agencyName", "");
            fieldValuesRef.current.agencyName = "";
        }
    }, [setValue]);

    const handleClassCodeNAChange = useCallback((e) => {
        const checked = e.target.checked;
        setValue("classCodeNA", checked);
        fieldValuesRef.current.classCodeNA = checked;

        if (checked) {
            setClassCodeInput("");
            setValue("classCode", "");
            fieldValuesRef.current.classCode = "";
        }
    }, [setValue]);

    const handleSignatureDateChange = useCallback((e) => {
        const value = e.target.value;
        setValue("signatureDate", value);
        fieldValuesRef.current.signatureDate = value;
    }, [setValue]);

    const handleValidationErrorClose = useCallback(() => {
        setValidationError("");
    }, []);

    const setComp = useCallback((index, field) => (e) => {
        const currentCompensation = fieldValuesRef.current.compensation || [];
        const updatedCompensation = currentCompensation.map((row, i) =>
            i === index ? { ...row, [field]: e.target.value } : row
        );
        setValue("compensation", updatedCompensation);
        fieldValuesRef.current.compensation = updatedCompensation;
    }, [setValue]);

    const handleSubmitClick = async () => {
        const values = fieldValuesRef.current;
        const isBlank = (value) => String(value ?? "").trim() === "";

        if (isBlank(values.orgName)) {
            setValidationError("Name of Employee Organization is missing for this Local. Please contact your Local to complete this information.");
            return;
        }

        if (isBlank(values.regNumber1) || isBlank(values.regNumber2)) {
            setValidationError("PERC Registration Number is missing for this Local. Please contact your Local to complete this information.");
            return;
        }

        if (isBlank(values.orderMonth) || isBlank(values.orderDay) || isBlank(values.orderYear)) {
            setValidationError("Date of Last Order Granting Registration is missing for this Local. Please contact your Local to complete this information.");
            return;
        }

        if (isBlank(values.initiationFee)) {
            setValidationError("Initiation Fee is missing for this Local. Please contact your Local to complete this information.");
            return;
        }

        const hasDues = ["duesWeekly", "duesBiweekly", "duesMonthly", "duesAnnually"].some((key) => !isBlank(values[key]));
        if (!hasDues) {
            setValidationError("At least one Dues amount (Weekly, Biweekly, Monthly, or Annually) is missing for this Local. Please contact your Local to complete this information.");
            return;
        }

        if (!values.agencySameAsEmployer && isBlank(values.agencyName)) {
            setValidationError("Please enter the Name of Agency");
            return;
        }

        if (isBlank(values.classTitle)) {
            setValidationError("Please enter a Class Title");
            return;
        }

        const classCodeValue = fieldValuesRef.current.classCode || "";
        const classCodeNAValue = fieldValuesRef.current.classCodeNA || false;

        if (!classCodeValue.trim() && !classCodeNAValue) {
            setValidationError("Please enter a Class Code or check 'Not Applicable'");
            return;
        }

        try {
            setValidationError("");
            setIsSubmitting(true);

            const formData = {
                ...fieldValuesRef.current,
                formVersion: 1,
                employeeId:
                    fieldValuesRef.current.employeeId
                    || membershipMaf?.employeeId
                    || "",
                LocChapFEAID:
                    fieldValuesRef.current.LocChapFEAID
                    || localUnionLocChapFEAID
                    || "",
                signature: signatureCanvasRef.current?.getTrimmedCanvas().toDataURL("image/png") || null,
                membershipId: membership?.id,
                membershipEntityTypeId: membership?.entityTypeId,
                memberMafId: membershipMaf?.id,
                mafSettingsId: membershipMaf?.mafSettings?.id,
                mafSettingsEntityTypeId: membershipMaf?.mafSettings?.entityTypeId,
                firstName: individual?.firstName || "",
                lastName: individual?.lastName || "",
            };

            await MemberService.submitMemberAuthorization(formData, cancelTokenRef.current?.token);
            onSubmit(formData);
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Error submitting member authorization form:", error);
                setValidationError("Error submitting form. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            {validationError && (
                <Alert severity="error" onClose={handleValidationErrorClose} sx={{ mb: 2 }}>
                    {validationError}
                </Alert>
            )}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
                <MemberAuthorizationFormBody
                    memberAuthorizationHeaderHtml={memberAuthorizationHeaderHtml}
                    memberAuthorizationFloridaNoticeHtml={memberAuthorizationFloridaNoticeHtml}
                    memberAuthorizationInstructionsHtml={memberAuthorizationInstructionsHtml}
                    formValues={formValues}
                    set={set}
                    setComp={setComp}
                    agencySameAsEmployer={agencySameAsEmployer}
                    handleAgencySameAsEmployerChange={handleAgencySameAsEmployerChange}
                    classTitleOptions={classTitleOptions}
                    classTitleInput={classTitleInput}
                    setClassTitleInput={setClassTitleInput}
                    setValue={setValue}
                    fieldValuesRef={fieldValuesRef}
                    classCodeOptions={classCodeOptions}
                    classCodeInput={classCodeInput}
                    setClassCodeInput={setClassCodeInput}
                    classCodeNA={classCodeNA}
                    handleClassCodeNAChange={handleClassCodeNAChange}
                    signatureCanvasRef={signatureCanvasRef}
                    signatureDate={formValues.signatureDate || ""}
                    onSignatureDateChange={handleSignatureDateChange}
                    onSignatureEmptyChange={setIsSignatureEmpty}
                />
            </Box>

            <Box
                sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    pt: 2,
                    mt: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "background.paper",
                }}
            >
                <Link component="button" onClick={onCancel} sx={{ textDecoration: "none" }} disabled={isSubmitting}>
                    Cancel
                </Link>
                <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={handleSubmitClick}
                    disabled={isSignatureEmpty || isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
            </Box>
        </Box>
    );
}

const MemberAuthorizationFormBody = React.memo(function MemberAuthorizationFormBody({
    memberAuthorizationHeaderHtml,
    memberAuthorizationFloridaNoticeHtml,
    memberAuthorizationInstructionsHtml,
    formValues,
    set,
    setComp,
    agencySameAsEmployer,
    handleAgencySameAsEmployerChange,
    classTitleOptions,
    classTitleInput,
    setClassTitleInput,
    setValue,
    fieldValuesRef,
    classCodeOptions,
    classCodeInput,
    setClassCodeInput,
    classCodeNA,
    handleClassCodeNAChange,
    signatureCanvasRef,
    signatureDate,
    onSignatureDateChange,
    onSignatureEmptyChange,
}) {
    const employeeIdText = valueToText(formValues.employeeId || "").trim();
    const escapedEmployeeId = employeeIdText
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const headerHtmlWithEmployeeId = employeeIdText
        ? memberAuthorizationHeaderHtml.replace(
            /(Please type or print legibly\.?)(?![^<]*<\/span>)/i,
            '$1 <span style="font-weight:600; font-style:italic;">' + escapedEmployeeId + "</span>"
        )
        : memberAuthorizationHeaderHtml;

    return (
        <Box>
            {memberAuthorizationHeaderHtml ? (
                <Box textAlign="center" mb={2} dangerouslySetInnerHTML={{ __html: headerHtmlWithEmployeeId }} />
            ) : null}

            <Paper variant="outlined" sx={{ mb: 3 }}>
                <Box sx={{ ...rowSx, textAlign: "center", bgcolor: "grey.50" }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textDecoration: "underline", textTransform: "uppercase" }}>
                        Part A &ndash; Employee Organization Information
                    </Typography>
                </Box>

                <Box sx={{ ...rowSx, display: "flex", alignItems: "flex-end", gap: 1 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        Name of Employee Organization:
                    </Typography>
                    <TextField variant="standard" fullWidth value={formValues.orgName || ""} onChange={set("orgName")} InputProps={{ readOnly: true }} />
                </Box>

                <Box sx={{ ...rowSx, display: "flex", alignItems: "flex-end", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        PERC Registration Number: OR-
                    </Typography>
                    <TextField variant="standard" value={formValues.regNumber1 || ""} onChange={set("regNumber1")} sx={{ width: 110 }} InputProps={{ readOnly: true }} />
                    <Typography variant="body2">-</Typography>
                    <TextField variant="standard" value={formValues.regNumber2 || ""} onChange={set("regNumber2")} sx={{ width: 110 }} InputProps={{ readOnly: true }} />
                </Box>

                <Box sx={{ ...rowSx, display: "flex", alignItems: "flex-end", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        Date of Last Order Granting Registration{" "}
                        <Typography component="span" variant="body2" fontWeight={400}>(mo/day/year):</Typography>
                    </Typography>
                    <TextField variant="standard" placeholder="MM" value={formValues.orderMonth || ""} onChange={set("orderMonth")} inputProps={{ maxLength: 2 }} sx={{ width: 36 }} InputProps={{ readOnly: true }} />
                    <Typography variant="body2">/</Typography>
                    <TextField variant="standard" placeholder="DD" value={formValues.orderDay || ""} onChange={set("orderDay")} inputProps={{ maxLength: 2 }} sx={{ width: 36 }} InputProps={{ readOnly: true }} />
                    <Typography variant="body2">/</Typography>
                    <TextField variant="standard" placeholder="YYYY" value={formValues.orderYear || ""} onChange={set("orderYear")} inputProps={{ maxLength: 4 }} sx={{ width: 56 }} InputProps={{ readOnly: true }} />
                </Box>

                <Box sx={{ ...rowSx, display: "flex", alignItems: "flex-end", gap: 1 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        Initiation Fee{" "}
                        <Typography component="span" variant="body2" fontWeight={400} fontStyle="italic">(if none, state zero)</Typography>:
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>$</Typography>
                    <TextField variant="standard" value={formValues.initiationFee || ""} onChange={set("initiationFee")} sx={{ width: 180 }} InputProps={{ readOnly: true }} />
                </Box>

                <Box sx={rowSx}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", mb: 1.5 }}>
                        Dues{" "}
                        <Typography component="span" variant="body2" fontWeight={400} fontStyle="italic">(write amount in applicable blank)</Typography>:
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Box display="flex" alignItems="flex-end" gap={1}>
                                <Typography variant="body2" fontWeight={700} whiteSpace="nowrap">Weekly:</Typography>
                                <TextField variant="standard" fullWidth value={formValues.duesWeekly || ""} onChange={set("duesWeekly")} InputProps={{ readOnly: true }} />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box display="flex" alignItems="flex-end" gap={1}>
                                <Typography variant="body2" fontWeight={700} whiteSpace="nowrap">Biweekly:</Typography>
                                <TextField variant="standard" fullWidth value={formValues.duesBiweekly || ""} onChange={set("duesBiweekly")} InputProps={{ readOnly: true }} />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box display="flex" alignItems="flex-end" gap={1}>
                                <Typography variant="body2" fontWeight={700} whiteSpace="nowrap">Monthly:</Typography>
                                <TextField variant="standard" fullWidth value={formValues.duesMonthly || ""} onChange={set("duesMonthly")} InputProps={{ readOnly: true }} />
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box display="flex" alignItems="flex-end" gap={1}>
                                <Typography variant="body2" fontWeight={700} whiteSpace="nowrap">Annually:</Typography>
                                <TextField variant="standard" fullWidth value={formValues.duesAnnually || ""} onChange={set("duesAnnually")} InputProps={{ readOnly: true }} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={rowSx}>
                    <Typography variant="body2" mb={1.5}>
                        <strong>OFFICER/EMPLOYEE COMPENSATION:</strong>{" "}
                        <Typography component="span" variant="body2" fontStyle="italic">
                            List the salaries, allowances, and other direct or indirect disbursements, exceeding $10,000.00
                            in the aggregate, paid by the employee organization and any affiliates to the{" "}
                            <strong>five highest compensated individuals</strong>, as reported on the last PERC registration
                            or renewal application. If fewer than five individuals are reported, indicate &ldquo;N/A&rdquo; on any remaining blank lines.
                        </Typography>
                    </Typography>
                    <Box sx={{ overflowX: "auto" }}>
                        <Table size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ ...colBorder, fontWeight: 700, fontSize: "0.75rem", width: "35%" }}>Name of Officer or Employee</TableCell>
                                    <TableCell sx={{ ...colBorder, fontWeight: 700, fontSize: "0.75rem" }}>Salary</TableCell>
                                    <TableCell sx={{ ...colBorder, fontWeight: 700, fontSize: "0.75rem" }}>Allowances</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Other direct or indirect disbursements (incl. reimbursed expenses)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(formValues.compensation || []).map((row, i) => (
                                    <TableRow key={i}>
                                        <TableCell sx={{ p: 0.5, ...colBorder }}>
                                            <TextField fullWidth size="small" variant="standard" value={row.name || "N/A"} onChange={setComp(i, "name")} InputProps={{ readOnly: true }} />
                                        </TableCell>
                                        {["salary", "allowances", "other"].map((col) => {
                                            const rawValue = row[col];
                                            const decimalOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
                                            const displayValue = rawValue ? parseFloat(rawValue).toLocaleString("en-US", decimalOptions) : "";
                                            return (
                                                <TableCell key={col} sx={{ p: 0.5, ...(col !== "other" ? colBorder : {}) }}>
                                                    <TextField
                                                        fullWidth size="small" variant="standard"
                                                        value={displayValue} onChange={setComp(i, col)}
                                                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, readOnly: true }}
                                                    />
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Box>

                <Box sx={{ ...rowSx, textAlign: "center", bgcolor: "grey.50" }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textDecoration: "underline", textTransform: "uppercase" }}>
                        Part B &ndash; Employee Information
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                        ***The employee must personally sign and complete the date of signature***
                    </Typography>
                </Box>

                <Box sx={{ ...rowSx, display: "flex", alignItems: "flex-end", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        Name of Employee{" "}
                        <Typography component="span" variant="body2" fontWeight={400} fontStyle="italic">(First Name&nbsp;&nbsp;Middle Name&nbsp;&nbsp;Last Name)</Typography>:
                    </Typography>
                    <TextField variant="standard" fullWidth value={formValues.employeeName || ""} onChange={set("employeeName")} sx={{ minWidth: 160, backgroundColor: "#fffde7" }} />
                </Box>

                <Box sx={{ ...rowSx, display: "flex", alignItems: "flex-end", gap: 1 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        Name of Public Employer:
                    </Typography>
                    <TextField variant="standard" fullWidth value={formValues.publicEmployer || ""} onChange={set("publicEmployer")} InputProps={{ readOnly: true }} />
                </Box>

                <Box sx={{ ...rowSx, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        Name of Agency:
                    </Typography>
                    <TextField variant="standard" sx={{ flex: 1, minWidth: 120, backgroundColor: "#fffde7" }} value={formValues.agencyName || ""} onChange={set("agencyName")} disabled={agencySameAsEmployer} />
                    <Box display="flex" alignItems="center" gap={0.5} ml={1}>
                        <Typography variant="body2" fontStyle="italic" whiteSpace="nowrap">
                            If the Agency is the same as the Public Employer, check here:
                        </Typography>
                        <Checkbox size="small" checked={agencySameAsEmployer} onChange={handleAgencySameAsEmployerChange} sx={{ p: 0.5 }} />
                    </Box>
                </Box>

                <Box sx={{ ...rowSx, display: "flex", alignItems: "flex-end", gap: 1 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        Class Title:
                    </Typography>
                    <Autocomplete
                        freeSolo
                        fullWidth
                        options={classTitleOptions}
                        inputValue={classTitleInput}
                        onInputChange={(_, value) => {
                            setClassTitleInput(value);
                            setValue("classTitle", value);
                            fieldValuesRef.current.classTitle = value;
                        }}
                        filterOptions={(options) => options}
                        renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select or type..." sx={{ backgroundColor: "#fffde7" }} />}
                    />
                </Box>

                <Box sx={{ ...rowSx, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        Class Code:
                    </Typography>
                    <Autocomplete
                        freeSolo
                        options={classCodeOptions}
                        getOptionLabel={(option) => typeof option === "string" ? option : option.label}
                        inputValue={classCodeInput}
                        disabled={classCodeNA}
                        sx={{ width: 220 }}
                        filterOptions={(options) => options}
                        onInputChange={(_, value, reason) => {
                            if (reason !== "reset") {
                                setClassCodeInput(value);
                                setValue("classCode", value);
                                fieldValuesRef.current.classCode = value;
                            }
                        }}
                        onChange={(_, value) => {
                            if (value !== null) {
                                const code = typeof value === "string" ? value : (value?.code || "");
                                setClassCodeInput(code);
                                setValue("classCode", code);
                                fieldValuesRef.current.classCode = code;
                            }
                        }}
                        renderInput={(params) => <TextField {...params} variant="standard" placeholder="Select or type..." sx={{ backgroundColor: "#fffde7" }} />}
                    />
                    <Box display="flex" alignItems="center" gap={0.5} ml={1}>
                        <Typography variant="body2" fontStyle="italic" whiteSpace="nowrap">
                            If not applicable, check here:
                        </Typography>
                        <Checkbox size="small" checked={classCodeNA} onChange={handleClassCodeNAChange} sx={{ p: 0.5 }} />
                    </Box>
                </Box>
            </Paper>

            <MemberAuthorizationSignatureSection
                signatureCanvasRef={signatureCanvasRef}
                setValue={setValue}
                signatureDate={signatureDate}
                onSignatureDateChange={onSignatureDateChange}
                onSignatureEmptyChange={onSignatureEmptyChange}
            />

            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                PERC Form 2023-1.101, Effective July 1, 2023, Incorporated in R. 60CC-1.101 &nbsp;&nbsp;
            </Typography>

            {memberAuthorizationFloridaNoticeHtml ? (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Box dangerouslySetInnerHTML={{ __html: memberAuthorizationFloridaNoticeHtml }} />
                </Paper>
            ) : null}

            {memberAuthorizationInstructionsHtml ? (
                <Accordion variant="outlined" sx={{ mb: 3 }} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="body2" fontWeight={700} sx={{ textTransform: "uppercase" }}>
                            Instructions for Completing Form 2023-1.101
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        <Box dangerouslySetInnerHTML={{ __html: memberAuthorizationInstructionsHtml }} />
                    </AccordionDetails>
                </Accordion>
            ) : null}
        </Box>
    );
});

const MemberAuthorizationSignatureSection = React.memo(function MemberAuthorizationSignatureSection({
    signatureCanvasRef,
    setValue,
    signatureDate,
    onSignatureDateChange,
    onSignatureEmptyChange,
}) {
    const handleClearSignature = useCallback(() => {
        if (signatureCanvasRef.current) {
            signatureCanvasRef.current.clear();
        }
        setValue("signature", "");
        onSignatureEmptyChange(true);
    }, [onSignatureEmptyChange, setValue, signatureCanvasRef]);

    const handleSignatureDrawn = useCallback(() => {
        if (!signatureCanvasRef.current || signatureCanvasRef.current.isEmpty()) {
            setValue("signature", "");
            onSignatureEmptyChange(true);
            return;
        }

        onSignatureEmptyChange(false);
    }, [onSignatureEmptyChange, setValue, signatureCanvasRef]);

    return (
        <>
            <Paper variant="outlined" sx={{ mb: 3 }}>
                <Box sx={rowSx}>
                    <Typography variant="body2">
                        By my signature below, I represent that I desire to be a member of the above-named employee organization.
                    </Typography>
                </Box>

                <Box sx={{ ...rowSx, borderBottom: "none" }}>
                    <ResizableSignatureCanvas
                        ref={(ref) => { signatureCanvasRef.current = ref; }}
                        clearOnResize={false}
                        onClear={handleClearSignature}
                        onEnd={handleSignatureDrawn}
                        canvasProps={{ style: { width: "100%", height: 200 } }}
                    />
                    <Typography variant="body2" fontWeight={700} textAlign="center" sx={{ textTransform: "uppercase", mt: 0.5 }}>
                        Signature of Employee
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                        <TextField
                            fullWidth variant="standard" type="date"
                            value={signatureDate} onChange={onSignatureDateChange}
                            InputProps={{ readOnly: true }}
                        />
                        <Typography variant="body2" fontWeight={700} textAlign="center" sx={{ textTransform: "uppercase", mt: 0.5 }}>
                            Date of Signature
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </>
    );
});

export default MemberAuthorization;
